import { motion } from "framer-motion"
import { formatDate } from "lopermut"
import { InfoIcon, RadarIcon } from "lucide-react"
import { ReactNode, useEffect, useMemo, useRef } from "react"
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  TooltipContentProps,
  TooltipPayloadEntry,
  XAxis,
  YAxis,
} from "recharts"
import { Tooltip, TooltipContent, TooltipTrigger } from "../../atoms/Tooltip"
import { CardContent } from "../../molecules/Card"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../molecules/Chart"
import { cn } from "../../utils"

const ACTIVITY_BAR_SIZE = 20

type SentimentDatum = {
  date?: string | null
  totalCount?: number
  avgSentiment?: number | null
  totalResponsesCount?: number
  VeryNegative?: number
  Negative?: number
  Neutral?: number
  NeutralP?: number
  NeutralN?: number
  Positive?: number
  VeryPositive?: number
}

type Props = {
  data?: SentimentDatum[]
  isPending: boolean
  chartConfig: ChartConfig
  tooltipInfo?: string
  noDataT?: {
    title: string
    description: string
  }
  action?: ReactNode
  chartClassName?: string
}

export const SentimentActivityChart = ({
  data,
  isPending,
  chartConfig,
  tooltipInfo,
  noDataT,
  action,
  chartClassName,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const noData = useMemo(() => {
    if (isPending) return false
    return !data || !data.length || data?.every((item) => item.totalCount === 0)
  }, [data])

  const computedData = useMemo(() => {
    return data?.map((item) => {
      let formattedDate = null
      try {
        formattedDate = item.date ? formatDate(item.date, "MMM d") : null
      } catch (_error) {
        // ignore formatting errors
      }

      const veryNegative = item["VeryNegative"] ?? 0
      const negative = item["Negative"] ?? 0
      const neutralP = item["NeutralP"] ?? 0
      const neutralN = item["NeutralN"] ?? 0
      const positive = item["Positive"] ?? 0
      const veryPositive = item["VeryPositive"] ?? 0

      // Prefer provided NeutralP / NeutralN. Fallback to splitting legacy Neutral evenly.
      const legacyNeutral = item["Neutral"]

      let neutralPositiveSplit = 0
      let neutralNegativeSplit = 0

      if (typeof neutralP === "number" && neutralP > 0) {
        neutralPositiveSplit = neutralP
      }
      if (typeof neutralN === "number" && neutralN > 0) {
        neutralNegativeSplit = neutralN
      }
      if (neutralPositiveSplit === 0 && neutralNegativeSplit === 0) {
        const neutralValue = typeof legacyNeutral === "number" ? legacyNeutral : 0
        neutralPositiveSplit = neutralValue / 2
        neutralNegativeSplit = neutralValue / 2
      }

      return {
        date: formattedDate,
        totalResponses: item.totalResponsesCount,
        average: item.avgSentiment != null ? item.avgSentiment.toFixed(1) : null,
        // Signed values to center X axis at 0 (value - 2 conceptual mapping)
        veryNegativeSigned: -veryNegative,
        negativeSigned: -negative,
        neutralNegative: -neutralNegativeSplit,
        neutralPositive: neutralPositiveSplit,
        positiveSigned: positive,
        veryPositiveSigned: veryPositive,
      }
    })
  }, [data])

  const fallbackData = useMemo(() => {
    if (!noData) return []
    const days = 30
    return Array.from({ length: days }, (_, i) => {
      const date = new Date()
      date.setDate(date.getDate() - (days - 1 - i))
      const formattedDate = formatDate(date, "MMM d")

      const veryNegative = 10 + ((i + Math.pow(-1, i)) % 3)
      const negative = 20 + ((i + Math.pow(-10, i)) % 4)
      const neutral = 30 + ((i + Math.pow(-10, i)) % 5)
      const positive = 18 + ((i + Math.pow(-10, i)) % 4)
      const veryPositive = 8 + ((i + Math.pow(-10, i)) % 3)

      // Split legacy neutral evenly for fallback data
      const neutralPositiveSplit = neutral / 2
      const neutralNegativeSplit = neutral / 2

      return {
        date: formattedDate,
        totalResponses: undefined,
        average: null,
        veryNegativeSigned: -veryNegative,
        negativeSigned: -negative,
        neutralNegative: -neutralNegativeSplit,
        neutralPositive: neutralPositiveSplit,
        positiveSigned: positive,
        veryPositiveSigned: veryPositive,
      }
    })
  }, [noData])

  const chartData = noData ? fallbackData : computedData

  const showTotalResponsesLine = !!chartConfig["totalResponses"]

  const responsesMax = useMemo(() => {
    if (!showTotalResponsesLine || !chartData?.length) return 0
    return Math.max(...chartData.map((d) => (d as { totalResponses?: number }).totalResponses || 0))
  }, [chartData, showTotalResponsesLine])

  // Symmetric domain around 0 so the reference line and Avg sentiment line align at 50%
  const domain = useMemo(() => {
    if (!chartData?.length) return [0, 0] as [number, number]
    let maxAbs = 0
    for (const d of chartData) {
      const pos = (d.neutralPositive || 0) + (d.positiveSigned || 0) + (d.veryPositiveSigned || 0)
      const neg = Math.abs(
        (d.neutralNegative || 0) + (d.negativeSigned || 0) + (d.veryNegativeSigned || 0),
      )
      maxAbs = Math.max(maxAbs, pos, neg)
    }
    const padded = Math.ceil(maxAbs * 1.1)
    return [-padded, padded] as [number, number]
  }, [chartData])

  useEffect(() => {
    if (containerRef.current && chartData?.length) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth
    }
  }, [chartData])

  return (
    <div className="flex flex-col w-full relative rounded-none border-none gap-2">
      <div
        className={cn(
          "flex flex-row items-center justify-between gap-4 pr-4",
          !action && "justify-end",
        )}
      >
        {action}
        <SentimentLegend
          className={cn(noData && "pointer-events-none grayscale opacity-15")}
          chartConfig={chartConfig}
          tooltipInfo={tooltipInfo}
        />
      </div>
      {noData && (
        <div className="flex items-center justify-center absolute inset-0 z-10 p-4">
          <div className="flex flex-col gap-2 py-14 items-center justify-center text-center m-auto max-w-sm">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <RadarIcon className="size-10 text-muted-foreground mb-3" strokeWidth={1.5} />
            </motion.div>
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <h2 className="text-md text-foreground text-stroke">{noDataT?.title}</h2>
            </motion.div>
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <p className="text-sm text-muted-foreground text-stroke-sm">{noDataT?.description}</p>
            </motion.div>
          </div>
        </div>
      )}
      <CardContent
        className={cn(
          "flex flex-col p-0 gap-2 items-end",
          noData && "pointer-events-none grayscale opacity-15",
        )}
      >
        <div ref={containerRef} className="w-full overflow-x-auto pb-2 -mb-2">
          <ChartContainer
            config={chartConfig}
            className={cn("aspect-auto h-[180px]", chartClassName)}
            style={{
              width: `${(chartData?.length ?? 0) * ACTIVITY_BAR_SIZE}px`,
              minWidth: "100%",
            }}
          >
            <ComposedChart
              accessibilityLayer
              data={chartData}
              margin={{ bottom: 0, top: 5 }}
              stackOffset="sign"
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={30}
              />
              <YAxis
                tickLine={true}
                axisLine={true}
                tickMargin={10}
                width={30}
                domain={domain}
                allowDataOverflow
                hide
              />
              <YAxis
                yAxisId="avg"
                orientation="right"
                domain={[0, 4]}
                width={30}
                tick={false}
                axisLine={false}
              />
              {showTotalResponsesLine && (
                <YAxis
                  yAxisId="responses"
                  orientation="right"
                  domain={[0, responsesMax * 1.1 * 6 || 1]}
                  width={30}
                  tick={false}
                  axisLine={false}
                />
              )}
              {showTotalResponsesLine && (
                <defs>
                  <linearGradient id="totalResponsesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--chart-4)" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="var(--chart-4)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="totalResponsesStrokeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--chart-4)" stopOpacity={1} />
                    <stop offset="100%" stopColor="var(--chart-4)" stopOpacity={0.3} />
                  </linearGradient>
                </defs>
              )}
              <ReferenceLine y={0} stroke="var(--foreground)" strokeOpacity={1} strokeWidth={0.5} />
              {showTotalResponsesLine && (
                <Area
                  type="monotone"
                  name={"totalResponses"}
                  dataKey="totalResponses"
                  yAxisId="responses"
                  fill="url(#totalResponsesGradient)"
                  fillOpacity={1}
                  stroke="url(#totalResponsesStrokeGradient)"
                  strokeWidth={1.5}
                  baseValue={0}
                  dot={false}
                  connectNulls
                />
              )}
              <ChartTooltip
                content={(props: TooltipContentProps) => {
                  const payload = props.payload || []

                  // Merge neutralPositive and neutralNegative under a single "neutral" item
                  const merged: TooltipPayloadEntry[] = []
                  let neutralTotal = 0
                  let neutralColor: string | undefined

                  for (const item of payload) {
                    if (item.dataKey === "neutralPositive" || item.dataKey === "neutralNegative") {
                      const v = typeof item.value === "number" ? Math.abs(item.value) : 0
                      neutralTotal += v
                      neutralColor = neutralColor || (item.color as string)
                      continue
                    }
                    merged.push(item)
                  }

                  if (neutralTotal > 0 && payload[0]) {
                    merged.push({
                      ...payload[0],
                      name: "neutral",
                      dataKey: "neutral",
                      color: neutralColor || "var(--neutral)",
                      value: neutralTotal,
                      payload: payload[0]?.payload,
                    })
                  }

                  const normalized = merged.map((item) =>
                    typeof item.value === "number"
                      ? { ...item, value: Math.abs(item.value) }
                      : item,
                  )

                  const formatted = normalized.map((item) =>
                    item.dataKey === "average" ? { ...item, value: `${item.value}/4` } : item,
                  )

                  const dataKeyToConfigKey: Record<string, string> = {
                    veryNegativeSigned: "veryNegative",
                    negativeSigned: "negative",
                    neutralPositive: "neutral",
                    neutralNegative: "neutral",
                    positiveSigned: "positive",
                    veryPositiveSigned: "veryPositive",
                  }
                  const withLabels = formatted.map((item) => {
                    const dataKey = String(item.dataKey ?? item.name)
                    const configKey = dataKeyToConfigKey[dataKey] ?? dataKey
                    // Gradient url() references don't render as tooltip indicator colors
                    const color =
                      typeof item.color === "string" && item.color.startsWith("url(")
                        ? "var(--chart-4)"
                        : item.color
                    return {
                      ...item,
                      color,
                      name: configKey,
                      dataKey: configKey,
                    }
                  })

                  return <ChartTooltipContent {...props} payload={withLabels} />
                }}
              />
              {/* Single stack; sign-aware stacking keeps negatives below 0 and positives above 0 */}
              <Bar
                name={"neutral"}
                dataKey="neutralNegative"
                stackId="s"
                fill={`var(--neutral)`}
                barSize={ACTIVITY_BAR_SIZE}
              />
              <Bar
                name={"negative"}
                dataKey="negativeSigned"
                stackId="s"
                fill={`var(--negative-soft)`}
                barSize={ACTIVITY_BAR_SIZE}
              />
              <Bar
                name={"veryNegative"}
                dataKey="veryNegativeSigned"
                stackId="s"
                fill={`var(--negative)`}
                barSize={ACTIVITY_BAR_SIZE}
              />
              {/* Positive side (same stack) */}
              <Bar
                name={"neutral"}
                dataKey="neutralPositive"
                stackId="s"
                fill={`var(--neutral)`}
                barSize={ACTIVITY_BAR_SIZE}
              />
              <Bar
                name={"positive"}
                dataKey="positiveSigned"
                stackId="s"
                fill={`var(--positive-soft)`}
                barSize={ACTIVITY_BAR_SIZE}
              />
              <Bar
                name={"veryPositive"}
                dataKey="veryPositiveSigned"
                stackId="s"
                fill={`var(--positive)`}
                barSize={ACTIVITY_BAR_SIZE}
              />
              <Line
                type="monotone"
                name={"average"}
                dataKey="average"
                yAxisId="avg"
                stroke="var(--testing-1)"
                strokeWidth={2}
                strokeDasharray="2 2"
                dot={false}
              />
            </ComposedChart>
          </ChartContainer>
        </div>
      </CardContent>
    </div>
  )
}

const SENTIMENT_GRADIENT =
  "linear-gradient(to right, var(--negative) 0%, var(--negative-soft) 25%, var(--neutral) 50%, var(--positive-soft) 75%, var(--positive) 100%)"

const SentimentLegend = ({
  className,
  chartConfig,
  tooltipInfo,
}: {
  className?: string
  chartConfig: ChartConfig
  tooltipInfo?: string
}) => {
  const avgLabel = (chartConfig["average"]?.label as string) || "Avg. sentiment"
  const scaleLabel =
    (chartConfig["sentimentScale"]?.label as string) ||
    (chartConfig["sentiment"]?.label as string) ||
    "Sentiment"
  const totalResponsesLabel = chartConfig["totalResponses"]?.label as string | undefined
  return (
    <div className={cn("flex items-center justify-end gap-4 flex-wrap", className)}>
      <div className="flex items-center gap-2">
        <div
          className="h-2 w-6 rounded-full shrink-0"
          style={{ background: SENTIMENT_GRADIENT }}
          title={scaleLabel}
        />
        <span className="text-sm text-muted-foreground whitespace-nowrap">{scaleLabel}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="size-2 rounded-full shrink-0" style={{ background: "var(--testing-1)" }} />
        <span className="text-sm text-muted-foreground">{avgLabel}</span>
      </div>
      {totalResponsesLabel && (
        <div className="flex items-center gap-1.5">
          <div className="size-2 rounded-full shrink-0" style={{ background: "var(--chart-4)" }} />
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {totalResponsesLabel}
          </span>
        </div>
      )}
      {tooltipInfo && (
        <Tooltip>
          <TooltipTrigger>
            <InfoIcon className="size-4 text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltipInfo}</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}
